import "./PageDashboard.css";
import { LocalDate, ZoneId } from "@js-joda/core";
import { type FC, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import comment from "../../src/report/comment.svg";
import followers from "../../src/report/followers.svg";
import like from "../../src/report/like.svg";
import share from "../../src/report/share.svg";
import { useCompany } from "../company/useCompany";
import { BarStackChartPosts } from "../components/BarStackChartPosts";
import { Card } from "../components/Card";
import { StatCard } from "../components/StatCard";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { usePostSuccessReport, useReport } from "../report/useReport";
import { ListingHealthCard } from "./ListingHealthCard";
import { RecentlyPublishedCard } from "./RecentlyPublishedCard";
import { ScheduledCard } from "./ScheduledCard";
import { WebsiteTrafficCard } from "./WebsiteTrafficCard";

export const PageDashboard: FC = () => {
	const company = useCompany();

	const today = useMemo(() => LocalDate.now(), []);
	const yesterday = useMemo(() => today.minusDays(1), [today]);
	const fromDate = useMemo(() => yesterday.minusDays(6), [yesterday]);

	const { totalFollowers, likes, comments, shares } = useReport([
		fromDate,
		yesterday,
	]);

	const fromPost = yesterday.minusDays(20);
	const toPost = today;
	const { data: postSuccess } = usePostSuccessReport(
		fromPost.toString(),
		toPost.toString(),
		ZoneId.systemDefault(),
	);

	return (
		<AppPage
			title="Dashboard"
			subtitle="Your marketing pulse — scheduled posts, listing health, and engagement."
		>
			<Row className="mb-3">
				<Col>
					{!company.onboarded && (
						<Link to={LinkGen.Onboard()} className="onboard-step">
							<div>Step 1</div>
							<div>Set Up Marketing Plan</div>
						</Link>
					)}
				</Col>
				<Col />
				<Col />
			</Row>
			<Row>
				<Col className="pb-4" md={12}>
					<ListingHealthCard />
				</Col>
				<Col className="pb-4" md={12}>
					<ScheduledCard />
				</Col>

				<Col className="pb-4" md={12}>
					<RecentlyPublishedCard />
				</Col>

				<Col className="pb-4" lg={3}>
					<StatCard
						title="Total Followers"
						value={totalFollowers ?? "-"}
						icon={<img src={followers} alt="" />}
					/>
				</Col>
				<Col lg={9} className="pb-4">
					<Card className="stat">
						<Card.Header>Engagement</Card.Header>
						<Card.Body>
							<div className="group-activity">
								<div className="group-item">
									<span className="group-title">Likes</span>
									<div className="group-content">
										<span className="group-metric-icon">
											<img src={like} alt="" />
										</span>
										<span className="group-number">{likes}</span>
									</div>
								</div>
								<div className="group-item">
									<span className="group-title">Comments</span>
									<div className="group-content">
										<span className="group-metric-icon">
											<img src={comment} alt="" />
										</span>
										<span className="group-number">{comments}</span>
									</div>
								</div>
								<div className="group-item">
									<span className="group-title">Shares</span>
									<div className="group-content">
										<span className="group-metric-icon">
											<img src={share} alt="" />
										</span>
										<span className="group-number">{shares}</span>
									</div>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col>
				<Col className="pb-4" md={12}>
					<WebsiteTrafficCard />
				</Col>
				<Col>
					<Card>
						<Card.Header>Successful posts based on platform</Card.Header>

						<Card.Body>
							<BarStackChartPosts posts={postSuccess ?? {}} />
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</AppPage>
	);
};
